import { useAuth0 } from "@auth0/auth0-react";
import { useQuery } from "@tanstack/react-query";
import { formatDistanceToNow, parseISO } from "date-fns";
import { useCallback } from "react";
import { NavLink, useNavigate, useSearchParams } from "react-router-dom";

import { useIsAdmin } from "@/hooks/useIsAdmin";
import { useResizablePanel } from "@/hooks/useResizablePanel";
import { useSessionSubject } from "@/hooks/useSessionSubject";
import { listSessions } from "@/services/api/sessions";
import { cn } from "@/utils/cn";

import { StatusPill } from "./StatusPill";
import { SubjectPanel } from "./SubjectPanel";

const SECTIONS = [
  { to: "/library", label: "Library" },
  { to: "/history", label: "History" },
  { to: "/activity", label: "Activity" },
  { to: "/spend", label: "Spend" },
  { to: "/profile", label: "Profile" },
];

interface Props {
  onCloseMobile?: () => void;
}

function relativeTime(iso: string | null): string {
  if (!iso) return "";
  try {
    return formatDistanceToNow(parseISO(iso), { addSuffix: true });
  } catch {
    return "";
  }
}

export function Sidebar({ onCloseMobile }: Props) {
  // 272px is the spec width; the bounds keep it usable at either extreme.
  const sidebar = useResizablePanel({
    storageKey: "eryndal.panel.sidebar",
    defaultWidth: 272,
    min: 220,
    max: 420,
    side: "left",
  });
  const { logout, isAuthenticated } = useAuth0();
  const { isAdmin } = useIsAdmin();
  const sections = isAdmin
    ? [...SECTIONS, { to: "/admin/users", label: "Admin" }]
    : SECTIONS;
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const activeSession = params.get("session");
  const forceClass = params.get("force_class") ?? "";

  const handleForceClassChange = useCallback(
    (value: string) => {
      setParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          if (value) next.set("force_class", value);
          else next.delete("force_class");
          return next;
        },
        { replace: true },
      );
    },
    [setParams],
  );

  const { data, isLoading } = useQuery({
    queryKey: ["sessions", "sidebar"],
    queryFn: () => listSessions(20),
    refetchInterval: 30_000,
  });
  const sessions = data ?? [];

  const subject = useSessionSubject(activeSession);

  const startNewChat = () => {
    navigate("/chat");
    onCloseMobile?.();
  };

  return (
    <aside
      style={{ width: sidebar.width }}
      className="relative flex h-full shrink-0 flex-col border-r border-ruleline bg-parchment-100"
    >
      {/* Grab handle on the trailing edge. Sits above the content, only wide
          enough to hit comfortably, and invisible until hovered. */}
      <div
        {...sidebar.separatorProps}
        aria-label="Resize sidebar"
        title="Drag to resize · double-click to reset"
        className={cn(
          "absolute right-0 top-0 z-20 h-full w-1.5 cursor-col-resize",
          "transition-colors duration-200 ease-atelier hover:bg-oxblood-200",
          "focus-visible:bg-oxblood-300 focus-visible:outline-none",
          sidebar.isDragging && "bg-oxblood-300",
        )}
      />
      <div className="border-b border-ruleline px-5 pb-4 pt-6">
        <NavLink to="/chat" className="block" onClick={onCloseMobile}>
          <span className="smallcaps text-oxblood-500">Eryndal</span>
          <h1 className="font-display text-[1.55rem] font-medium leading-[1.1] tracking-[-0.02em] text-espresso-700">
            S<span className="text-oxblood-500">ai</span>gar's Bot
          </h1>
        </NavLink>
      </div>

      <div className="px-4 pt-4">
        <button type="button" onClick={startNewChat} className="btn-oxblood h-[38px] w-full">
          <span className="text-base leading-none">+</span> New chat
        </button>
      </div>

      <div className="mt-6 flex-1 overflow-y-auto px-2">
        {activeSession ? (
          <SubjectPanel
            subjectState={subject.data?.subject_state ?? {
              active_topics: [],
              primary_entities: [],
              open_threads: [],
              last_user_intent: "",
            }}
            sessionId={activeSession}
            forceClass={forceClass}
            onForceClassChange={handleForceClassChange}
            disabled={subject.isDropping}
            onDropTopic={(topic) => subject.drop({ drop_topic: topic })}
            onDropEntity={(entity) => subject.drop({ drop_entity: entity })}
            onDropThread={(thread) => subject.drop({ drop_thread: thread })}
          />
        ) : null}

        <div className="label-mono-muted px-3 pb-2">Recent</div>
        {isLoading ? (
          <p className="px-3 text-xs italic text-warmgray-400">Loading…</p>
        ) : sessions.length === 0 ? (
          <p className="px-3 text-xs italic text-warmgray-400">No conversations yet.</p>
        ) : (
          <ul className="space-y-0.5">
            {sessions.map((s) => {
              const isActive = activeSession === s.session_id;
              return (
                <li key={s.session_id}>
                  <NavLink
                    to={`/chat?session=${encodeURIComponent(s.session_id)}`}
                    onClick={onCloseMobile}
                    className={cn(
                      "block rounded-chip border-l-2 border-transparent px-3 py-2 text-sm",
                      "transition-colors duration-200 ease-atelier hover:bg-parchment-50",
                      isActive && "border-oxblood-500 bg-parchment-50",
                    )}
                  >
                    <div className="truncate text-espresso-700">
                      {s.title?.trim() || "Untitled conversation"}
                    </div>
                    <div className="mt-0.5 text-[0.65rem] text-warmgray-400">
                      {relativeTime(s.last_active_at ?? s.created_at)}
                      {s.turn_count ? ` · ${s.turn_count} turns` : ""}
                    </div>
                  </NavLink>
                </li>
              );
            })}
          </ul>
        )}

        <div className="label-mono-muted mt-6 px-3 pb-2">Sections</div>
        <ul className="space-y-0.5">
          {sections.map((item) => (
            <li key={item.to}>
              <NavLink
                to={item.to}
                onClick={onCloseMobile}
                className={({ isActive }) =>
                  cn(
                    "block rounded-chip px-3 py-2 font-serif text-sm text-warmgray-500",
                    "transition-colors duration-200 ease-atelier",
                    "hover:bg-parchment-50 hover:text-oxblood-500",
                    isActive && "text-oxblood-500",
                  )
                }
              >
                {item.label}
              </NavLink>
            </li>
          ))}
        </ul>
      </div>

      <div className="border-t border-ruleline px-4 py-4">
        <StatusPill />
        {isAuthenticated ? (
          <button
            type="button"
            onClick={() => logout({ logoutParams: { returnTo: window.location.origin } })}
            className="mt-3 w-full text-left text-xs text-warmgray-400 hover:text-oxblood-500"
          >
            Sign out
          </button>
        ) : null}
      </div>
    </aside>
  );
}
